import React, { useEffect, useState, useCallback } from "react";
import { Users, Loader2, UserPlus, Copy, Check, Trash2, Crown, ShieldCheck, User as UserIcon, Zap, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_ICON = { owner: Crown, admin: ShieldCheck, member: UserIcon };

function inviteLink(token) {
  return `${window.location.origin}/join/${token}`;
}

function MemberRow({ m, canManage, onRemove, onRole }) {
  const Icon = ROLE_ICON[m.role] || UserIcon;
  const initials = (m.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-background/50 p-3" data-testid={`team-member-${m.user_id}`}>
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent">{initials}</div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{m.name}{m.is_you && <span className="text-muted-foreground"> (you)</span>}</p>
        <p className="truncate text-xs text-muted-foreground">{m.email}</p>
      </div>
      {m.is_owner || !canManage ? (
        <Badge variant="secondary" className="gap-1 capitalize"><Icon className="h-3 w-3" /> {m.role}</Badge>
      ) : (
        <div className="flex items-center gap-2">
          <Select value={m.role} onValueChange={(v) => onRole(m.user_id, v)}>
            <SelectTrigger className="h-8 w-[110px]" data-testid={`member-role-${m.user_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onRemove(m)} data-testid={`remove-member-${m.user_id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function InviteRow({ inv, onRevoke }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(inviteLink(inv.token));
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-background/50 p-3" data-testid={`invite-row-${inv.id}`}>
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-muted-foreground"><UserPlus className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">Pending invite · <span className="capitalize">{inv.role}</span></p>
        <p className="truncate text-xs text-muted-foreground">{inviteLink(inv.token)}</p>
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={copy} data-testid={`copy-invite-${inv.id}`}>
        {copied ? <Check className="h-4 w-4 text-accent" /> : <Copy className="h-4 w-4" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onRevoke(inv.id)} data-testid={`revoke-invite-${inv.id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function TeamPanel({ onUpgrade }) {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("member");
  const [inviteEmail, setInviteEmail] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team");
      setTeam(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const email = inviteEmail.trim();
      const { data } = await api.post("/team/invites", { role, email: email || undefined, base_url: window.location.origin });
      navigator.clipboard.writeText(inviteLink(data.token)).catch(() => {});
      if (data.emailed) toast.success(`Invite emailed to ${data.email} (link also copied).`);
      else if (email) toast.warning(`Couldn't email ${email} — the invite link was copied to your clipboard instead.`);
      else toast.success("Invite link created & copied — share it however you like.");
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async (id) => {
    try { await api.delete(`/team/invites/${id}`); load(); toast.success("Invite revoked"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try { await api.delete(`/team/members/${m.user_id}`); load(); toast.success(`${m.name} removed`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const changeRole = async (id, r) => {
    try { await api.patch(`/team/members/${id}`, { role: r }); load(); toast.success("Role updated"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  if (!team) return null;

  const { seats, members, invites, can_manage } = team;
  const pending = invites?.length || 0;
  const full = seats.used + pending >= seats.limit;

  return (
    <div className="mt-8" data-testid="team-panel">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-wider">Team</span></div>
        <Badge variant="secondary" data-testid="seat-usage">{seats.used} of {seats.limit} seat{seats.limit > 1 ? "s" : ""} used</Badge>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
        <div className="space-y-2.5">
          {members.map((m) => (
            <MemberRow key={m.user_id} m={m} canManage={can_manage} onRemove={removeMember} onRole={changeRole} />
          ))}
          {invites.map((inv) => (
            <InviteRow key={inv.id} inv={inv} onRevoke={revoke} />
          ))}
        </div>

        {can_manage && (
          <div className="mt-5 border-t border-border pt-5">
            {full ? (
              <div className="flex flex-col items-start gap-3 rounded-xl bg-secondary/60 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="seats-full-cta">
                <div>
                  <p className="text-sm font-semibold">You've used all your seats</p>
                  <p className="text-xs text-muted-foreground">
                    {seats.plan === "business" ? "Business includes up to 5 seats." : `Upgrade to ${seats.plan === "free" ? "Pro (2 seats)" : "Business (up to 5 seats)"} to invite more teammates.`}
                  </p>
                </div>
                {seats.plan !== "business" && (
                  <Button size="sm" onClick={() => onUpgrade(seats.plan === "free" ? "pro" : "business")} data-testid="seats-upgrade-button">
                    <Zap className="mr-2 h-4 w-4" /> Upgrade
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div>
                  <p className="mb-1.5 text-sm font-medium">Invite a teammate</p>
                  <p className="text-xs text-muted-foreground">Enter an email to send an invite directly, or leave it blank to just generate a shareable link.</p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative flex-1">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@email.com (optional)" className="pl-9" data-testid="invite-email-input" />
                  </div>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="w-full sm:w-[130px]" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={generate} disabled={generating} data-testid="generate-invite-button">
                    {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : (inviteEmail.trim() ? <Mail className="mr-2 h-4 w-4" /> : <UserPlus className="mr-2 h-4 w-4" />)}
                    {inviteEmail.trim() ? "Send invite" : "Create link"}
                  </Button>
                </div>
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Admins</span> can manage the team and all data. <span className="font-medium text-foreground">Members</span> can do day-to-day work but can't manage the team or billing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
